
import { WellnessCard } from "@/components/WellnessCard";
import { LayoutDashboard, Moon } from "lucide-react";
import { useWellness } from "@/context/WellnessContext";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export function OverviewSection() {
  const { 
    nutritionPlan, 
    supplementPlan, 
    fitnessPlan, 
    mindfulnessPlan,
    calculateWellnessScore,
    getSleepScore
  } = useWellness();
  
  const overallScore = calculateWellnessScore();
  
  return (
    <div className="space-y-6">
      <WellnessCard
        title="Overall Wellness Score"
        icon={<LayoutDashboard className="h-5 w-5" />}
        category="overall"
      >
        <div className="flex flex-col items-center justify-center py-6">
          <div className="text-5xl font-bold mb-2">{overallScore}</div>
          <p className="text-muted-foreground text-sm">
            {overallScore > 80 
              ? "Your wellness journey is going great!" 
              : overallScore > 60 
                ? "You're making good progress!" 
                : "You're on your way to better wellness!"}
          </p>
        </div>
      </WellnessCard>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Nutrition</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Progress</span>
                <span className="text-sm">{nutritionPlan.progress}%</span>
              </div>
              <Progress value={nutritionPlan.progress} className="h-2" />
              {nutritionPlan.progress === 0 && (
                <p className="text-sm text-muted-foreground mt-2">Start logging your meals to track your nutrition progress!</p>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Fitness</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Progress</span>
                <span className="text-sm">{fitnessPlan.progress}%</span>
              </div>
              <Progress value={fitnessPlan.progress} className="h-2" />
              {fitnessPlan.progress === 0 && (
                <p className="text-sm text-muted-foreground mt-2">Log your workouts to start tracking your fitness progress!</p>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Supplements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Progress</span>
                <span className="text-sm">{supplementPlan.progress}%</span>
              </div>
              <Progress value={supplementPlan.progress} className="h-2" />
              {supplementPlan.progress === 0 && (
                <p className="text-sm text-muted-foreground mt-2">Start tracking your supplements to monitor your regimen!</p>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Mindfulness</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Progress</span>
                <span className="text-sm">{mindfulnessPlan.progress}%</span>
              </div>
              <Progress value={mindfulnessPlan.progress} className="h-2" />
              {mindfulnessPlan.progress === 0 && (
                <p className="text-sm text-muted-foreground mt-2">Begin your mindfulness journey by logging your practices!</p>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Sleep</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Score</span>
                <span className="text-sm">{getSleepScore()}%</span>
              </div>
              <Progress value={getSleepScore()} className="h-2" />
              <p className="text-sm text-muted-foreground mt-2">
                {getSleepScore() > 80 
                  ? "Your sleep quality is excellent!" 
                  : getSleepScore() > 60 
                    ? "Your sleep quality is good" 
                    : "Track your sleep to improve your sleep quality"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
